import { Injectable, Injector } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { FwLegacyDialogService as DialogService } from '../../dialogs/dialog.service';
import { FileVersionDialog } from '../../files/file-version-dialog/file-version-dialog.component';
import { ApiHeaders, ApiParams, CoreApiService, WriteResult } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { downloadFile } from '../../shared/browser.utils';
import { getContainerProjectId } from '../../shared/container.utils';
import { Container } from '../../shared/models/container.model';
import { DownloadTicket } from '../../shared/models/download.model';
import { ArchiveMemberInfo, File, FileType } from '../../shared/models/file.model';
import { omitEmptyParams } from '../../shared/url.utils';
// TODO: add back this dialog
// import { UploadDialogComponent } from '../../upload/upload-dialog/upload-dialog.component';
import { ViewerFrameDialog } from '../../viewers/frame/viewer-frame-dialog.component';
import { ViewerService } from '../../viewers/viewer.service';
import { UploadDialogComponent } from '../../upload/upload-dialog/upload-dialog.component';

const editorAppName = 'Code Editor';

export interface FileIdentifier {
  containerId?: string
  fileName?: string
  fileId?: string
  version?: number
}

export interface DownloadOptions extends FileIdentifier {
  fileName: string
  ticket?: string
}

export interface UploadOptions {
  containerId: string
  files: File[]
}

@Injectable({
  providedIn: 'root',
})
export class ContainerAttachmentsService {

  constructor(
    private config: ConfigService,
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private injector: Injector,
    private logger: LoggingService,
    private notifications: NotificationService,
  ) {}

  /**
   * Get file record either by fileId or (containerId and fileName)
   */
  metadata({ containerId, fileId, fileName, version }: FileIdentifier): Observable<File> {
    const params = omitEmptyParams({ version });
    return fileId ? this.coreApi.files.get(fileId, params) : this.coreApi.containers.files.info.get(containerId, fileName, params);
  }

  /**
   * Get download ticket either by fileId or (containerId and fileName)
   */
  ticket({ containerId, fileId, fileName }: FileIdentifier, view = false): Observable<DownloadTicket> {
    const params = { view };
    return fileId ? this.coreApi.files.ticket(fileId, params) : this.coreApi.containers.files.ticket(containerId, fileName);
  }

  downloadUrl(options: FileIdentifier & { ticket?: string }, view = false): Observable<string> {
    const { containerId, fileId, fileName, version } = options;
    return (options.ticket ? of({ ticket: options.ticket }) : this.ticket(options, view)).pipe(
      map(({ ticket }) => {
        const params = new URLSearchParams(omitEmptyParams({ ticket, version }) as Record<string, string>);
        return this.config.getApiUrl(fileId
          ? `/api/files/${fileId}/download?${String(params)}`
          : `/api/containers/${containerId}/files/${encodeURIComponent(fileName)}?${String(params)}}`);
      }));
  }

  /**
   * Download a file either by fileId or (containerId and fileName), requiring fileName to set the name of the download.
   */
  download(options: DownloadOptions): void {
    const { fileName } = options;
    this.downloadUrl(options).subscribe({
      next: fileUrl => {
        downloadFile(fileName, fileUrl);
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('container.file.download.error');
      },
    });
  }

  blob({ containerId, fileId, fileName, version }: FileIdentifier, params?: ApiParams, headers?: ApiHeaders): Observable<Blob> {
    params = { ...omitEmptyParams({ version }), ...params };
    return fileId
      ? this.coreApi.files.blob(fileId, params, headers)
      : this.coreApi.containers.files.blob(containerId, fileName, params, headers);
  }

  zipInfo({ containerId, fileId, fileName, version }: FileIdentifier, params?: ApiParams): Observable<ArchiveMemberInfo> {
    params = { ...omitEmptyParams({ version }), ...params };
    return fileId ? this.coreApi.files.zipInfo(fileId, params) : this.coreApi.containers.files.members(containerId, fileName, params);
  }

  zipMember({ containerId, fileId, fileName, version }: FileIdentifier, memberPath: string, params?: ApiParams, headers?: ApiHeaders): Observable<Blob> {
    params = { ...omitEmptyParams({ version }), ...params };
    return fileId
      ? this.coreApi.files.zipMember(fileId, memberPath, params, headers)
      : this.coreApi.containers.files.blob(containerId, fileName, { ...params, member: true }, headers);
  }

  zipMemberUrl(options: FileIdentifier & { ticket?: string }, memberPath: string): Observable<string> {
    const { containerId, fileId, fileName, version } = options;
    return (options.ticket ? of({ ticket: options.ticket }) : this.ticket(options)).pipe(
      map(({ ticket }) => {
        const params = new URLSearchParams(omitEmptyParams({ ticket, version, view: true }) as Record<string, string>);
        if (fileId) {
          return this.config.getApiUrl(`/api/files/${fileId}/zip_member/${encodeURIComponent(memberPath)}?${String(params)}`);
        }
        params.set('member', String(memberPath));
        return this.config.getApiUrl(`/api/containers/${containerId}/files/${encodeURIComponent(fileName)}?${String(params)}`);
      }));
  }

  getFileIdentifierFromUrl(fileUrl: string): FileIdentifier {
    const url = new URL(fileUrl, window.location.href);
    const segments = url.pathname.split('/');
    const version = url.searchParams.get('version');
    if (segments[2] === 'files') {
      // /api/files/:fileId
      return omitEmptyParams({ fileId: segments[3], version });
    }
    // /api/containers/:containerId/files/:fileName
    return omitEmptyParams({ containerId: segments[3], fileName: decodeURIComponent(segments[5]), version });
  }

  /**
   * Delete a file either by fileId or (containerId and fileName)
   */
  delete({ containerId, fileId, fileName }: FileIdentifier, params?: { version?: number, delete_reason?: string, force?: boolean }): Observable<WriteResult> {
    // for delete we want to default to deleting all versions by not passing in the `version` unless it is an explicit param
    return fileId ? this.coreApi.files.delete(fileId, params) : this.coreApi.containers.files.delete(containerId, fileName, params);
  }

  createFile(parentContainer: Container): Promise<void> {
    const projectId = getContainerProjectId(parentContainer);
    // lazy load circular dependency for ViewerService
    return this.injector.get(ViewerService).getAssociatedApps({
      projectId,
      file: { type: FileType.SourceCode } as File,
    }).pipe(
      switchMap(apps => {
        const editorApp = apps.find(app => app.name === editorAppName);
        if (editorApp) {
          return this.dialog.open(ViewerFrameDialog, {
            panelClass: 'padding-0',
            data: {
              applicationUrl: editorApp.url,
              container: parentContainer,
            },
          }).afterClosed();
        }
        throw new Error('Cannot resolve file editor application');
      }),
    ).toPromise() as Promise<void>;
  }

  upload({ containerId }: UploadOptions): Promise<unknown> {
    const uploadDialog = this.dialog.open(UploadDialogComponent, {
      data: { containerId },
      width: '600px',
    });

    return firstValueFrom(uploadDialog.afterClosed());
  }

  versionHistory(file: File): Promise<boolean> {
    return this.dialog.open(FileVersionDialog, {
      data: { file },
      disableClose: false,
      hasBackdrop: true,
      position: { right: '0' },
      panelClass: ['border-radius-0', 'padding-0'],
    }).afterClosed().toPromise() as Promise<boolean>;
  }
}
